/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { DataOptions } from "vuetify";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { User } from "@/models/api/administration/users/User";
import {
  addAgentCode,
  addUser,
  addUserProfileCashbox,
  addUserProfileDefaultCashbox,
  addUserRole,
  addUserToOffice,
  editAgentCode,
  editUserBasicInfo,
  getAgentCodes,
  getUserBasicInfo,
  getUserCashboxes,
  getUserOfficeRoles,
  getUserOffices,
  getUserOfficesToAdd,
  getUsersTable,
  lockUser,
  removeAgentCode,
  removeUserFromOffice,
  removeUserProfileCashbox,
  removeUserRole,
  unlockUser,
} from "@/requests/administration/administrationUsers";
import _ from "lodash";
import { FILTER_NAMES } from "@/static/filterNames";
import { ADMINISTRATION_USERS_TABS } from "@/components/administration/users/AdministrationUsersContentMenu.vue";
import { Office } from "@/models/api/administration/users/Office";
import { SnackbarModel } from "@/models/components/popup/Snackbar";
import { UserBasicInfo } from "@/models/api/administration/users/UserBasicInfo";
import { UserRole } from "@/models/api/administration/users/UserRole";
import { AgentCode } from "@/models/api/administration/users/AgentCode";
import { UserCashbox } from "@/models/api/administration/users/UserCashbox";
import { UserOfficeProfile } from "@/models/api/administration/users/UserOfficeProfile";
import { NewUser } from "@/models/api/administration/users/NewUser";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import { OfficeProfileResult } from "@/models/api/administration/servicesAndAttributes/ServiceTypeExportConfiguration";
import { TemporaryRole } from "@/models/api/administration/temporaryRoles/TemporaryRole";
import { SelectItem } from "@/models/api/SelectItem";
import {
  addTemporaryRole,
  editTemporaryRole,
  getRoles,
  getUserTemporaryRoles,
  toggleTemporaryRole,
} from "@/requests/administration/administrationTemporaryRoles";
import { EditTemporaryRoleModel } from "@/models/api/administration/temporaryRoles/EditTemporaryRole";

export interface AdministrationUsersStateInterface {
  isUsersLoading: boolean;
  users: Array<User>;
  usersFilters: null | Array<CriterionBase>;
  dataTableOptions: DataOptions;
  selectedUserId: null | string;
  selectedTab: ADMINISTRATION_USERS_TABS;
  userOffices: Array<Office>;
  userOfficesToAdd: Array<Office>;
  selectedOfficeId: null | string;
  isAddToOfficeDialog: boolean;
  userBasicInfo: UserBasicInfo | null;
  userProfiles: Array<UserOfficeProfile>;
  userOfficeRoles: Array<UserRole>;
  agentCodes: Array<AgentCode>;
  isAgentCodeDialog: boolean;
  agentCodeId: null | number;
  agentId: string;
  agentSystem: string;
  cashboxes: Array<UserCashbox>;
  isUserAddDialog: boolean;
  officeProfiles: Array<OfficeProfileResult>;
  temporaryRoles: Array<TemporaryRole>;
  roles: Array<SelectItem>;
  recordStampId: number | null;
  editTemporaryRole: EditTemporaryRoleModel | null;
}

export const AdministrationUsersState: AdministrationUsersStateInterface = {
  isUsersLoading: false,
  users: [],
  usersFilters: null,
  dataTableOptions: new DefaultDataOptions(),
  selectedUserId: null,
  selectedTab: ADMINISTRATION_USERS_TABS.BasicInformation,
  userOffices: [],
  userOfficesToAdd: [],
  selectedOfficeId: null,
  isAddToOfficeDialog: false,
  userBasicInfo: null,
  userProfiles: [],
  userOfficeRoles: [],
  agentCodes: [],
  isAgentCodeDialog: false,
  agentCodeId: null,
  agentId: "",
  agentSystem: "",
  cashboxes: [],
  isUserAddDialog: false,
  officeProfiles: [],
  temporaryRoles: [],
  roles: [],
  recordStampId: null,
  editTemporaryRole: null,
};

const AdministrationUsersModule = defineModule({
  state: AdministrationUsersState as AdministrationUsersStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    startUsersLoading(state) {
      state.isUsersLoading = true;
    },
    setUsers(state, param: Array<User>) {
      state.users = param;
      state.isUsersLoading = false;
    },
    setUsersFilters(state, param: Array<CriterionBase>) {
      state.usersFilters = param;
    },
    setSelectedUserId(state, param: null | string) {
      if (state.selectedUserId !== param) {
        state.selectedOfficeId = null;
        state.selectedUserId = param;
      }
    },
    setSelectedTab(state, param: ADMINISTRATION_USERS_TABS) {
      state.selectedTab = param;
    },
    setUserOffices(state, param: Array<Office>) {
      state.userOffices = param;
    },
    setUserOfficesToAdd(state, param: Array<Office>) {
      state.userOfficesToAdd = param;
    },
    setSelectedOfficeId(state, param: string | null) {
      state.selectedOfficeId = param;
    },
    setIsAddToOfficeDialog(state, param: boolean) {
      state.isAddToOfficeDialog = param;
      if (!param) {
        state.userOfficesToAdd = [];
      }
    },
    setUserBasicInfo(state, param: UserBasicInfo | null) {
      state.userBasicInfo = param;
    },
    setUserProfiles(state, param: Array<UserOfficeProfile>) {
      state.userProfiles = param;
    },
    setUserOfficeRoles(state, param: Array<UserRole>) {
      state.userOfficeRoles = param;
    },
    setAgentCodes(state, param: Array<AgentCode>) {
      state.agentCodes = param;
    },
    setIsAgentCodeDialog(state, param: boolean) {
      state.isAgentCodeDialog = param;
    },
    setAgentCodeId(state, param: null | number) {
      state.agentCodeId = param;
    },
    setAgentId(state, param: string) {
      state.agentId = param;
    },
    setAgentSystem(state, param: string) {
      state.agentSystem = param;
    },
    setCashboxes(state, param: Array<UserCashbox>) {
      state.cashboxes = param;
    },
    setIsUserAddDialog(state, param: boolean) {
      state.isUserAddDialog = param;
    },
    setDataTableOptions(state, param: DataOptions) {
      state.dataTableOptions = param;
    },
    setOfficeProfile(state, param: Array<OfficeProfileResult>) {
      state.officeProfiles = param;
    },
    setTemporaryRoles(state, param: Array<TemporaryRole>) {
      state.temporaryRoles = param;
    },
    setRoles(state, param: Array<SelectItem>) {
      state.roles = param;
    },
    setRecordStampId(state, param: number | null) {
      state.recordStampId = param;
    },
    setEditTemporaryRole(state, param: EditTemporaryRoleModel | null) {
      state.editTemporaryRole = param;
    },
  },
  actions: {
    async downloadUsers(context, skipSetDefault: boolean | undefined) {
      const { commit, state } = _moduleActionContext(context);
      commit.startUsersLoading();
      let users = await getUsersTable({
        count: state.dataTableOptions.itemsPerPage,
        page: state.dataTableOptions.page - 1,
        criterions: _.cloneDeep(state.usersFilters),
        filterName: FILTER_NAMES.UserFilter,
      });
      users = users.sort((a, b) => a.userName.localeCompare(b.userName));
      commit.setUsers(users);
      if (!skipSetDefault && users.length > 0) {
        commit.setSelectedUserId(users.sort()[0].id);
      }
    },
    async downloadUserOffices(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedUserId !== null) {
        const userOffices = await getUserOffices(state.selectedUserId as string);
        commit.setUserOffices(userOffices);
        if (userOffices.length > 0) {
          commit.setSelectedOfficeId(userOffices[0].id);
        }
      }
    },
    async downloadUserBasicInfo(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedUserId !== null && state.selectedOfficeId !== null) {
        const userBasicInfo = await getUserBasicInfo(state.selectedUserId as string, state.selectedOfficeId as string);
        commit.setUserBasicInfo(userBasicInfo.basicInfo);
        commit.setUserProfiles(userBasicInfo.userOfficeProfiles);
      }
    },
    async saveUserBasicInfo(context) {
      const { dispatch, rootDispatch, state } = _moduleActionContext(context);
      if (state.selectedUserId !== null && state.selectedOfficeId !== null && state.userBasicInfo !== null) {
        await rootDispatch.failIfRequestsInProgress();
        await editUserBasicInfo(state.selectedUserId, state.selectedOfficeId, state.userBasicInfo);
        rootDispatch.popup.addSavedOkSnackbar();
        await dispatch.downloadUserBasicInfo();
      }
    },
    async addUser(context, user: NewUser) {
      const { dispatch, rootDispatch, commit } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      const addedUserId = await addUser(user);
      // noinspection SuspiciousTypeOfGuard
      if (typeof addedUserId !== "string") {
        throw new Error("User not added");
      }
      commit.setIsUserAddDialog(false);
      rootDispatch.popup.addSavedOkSnackbar();
      await dispatch.downloadUsers(true);
      commit.setSelectedUserId(addedUserId);
    },
    async downloadUserOfficesToAdd(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedUserId !== null) {
        const userOfficesToAdd = await getUserOfficesToAdd(state.selectedUserId);
        commit.setUserOfficesToAdd(userOfficesToAdd);
      }
    },
    async openAddToOfficeDialog(context) {
      const { commit, state, dispatch, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.waitForCompleteAllRequests();
      if (state.selectedUserId !== null) {
        await dispatch.downloadUserOfficesToAdd();
        commit.setIsAddToOfficeDialog(true);
      }
    },
    async addUserToOffice(context, officeId: string) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      if (state.selectedUserId !== null) {
        await rootDispatch.failIfRequestsInProgress();
        await addUserToOffice(state.selectedUserId, officeId);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
        // noinspection ES6MissingAwait
        dispatch.downloadUserOfficesToAdd();
        dispatch.downloadUserOffices();
      }
    },
    async removeUserFromOffice(context) {
      const { rootGetters, state, dispatch, rootDispatch } = _moduleActionContext(context);
      if (rootGetters.isLoading) {
        rootDispatch.popup.addWaitTillOperationsCompleteSnackbar();
      } else if (state.selectedUserId !== null) {
        if (state.selectedOfficeId === null) {
          rootDispatch.popup.addSnackbar(new SnackbarModel("officeIsNotSelected", "error"));
        } else {
          const confirmModel = new ConfirmModel(
            filterCapitalize(i18n.t("doYouReallyWantToRemoveUserFromOffice")),
            filterCapitalize(i18n.t("warning")),
            [
              {
                text: filterCapitalize(i18n.t("no")),
                color: "warning",
                value: false,
                outlined: true,
              },
              {
                text: filterCapitalize(i18n.t("yes")),
                color: "warning",
                value: true,
              },
            ],
            "450px"
          );
          if (await rootDispatch.popup.addConfirm(confirmModel)) {
            await removeUserFromOffice(state.selectedUserId, state.selectedOfficeId);
            dispatch.downloadUserOffices();
            rootDispatch.popup.addOperationSuccessfulSnackbar();
          }
        }
      }
    },
    async toggleUserLock(context) {
      const { rootGetters, state, dispatch, rootDispatch } = _moduleActionContext(context);
      if (rootGetters.isLoading) {
        rootDispatch.popup.addWaitTillOperationsCompleteSnackbar();
      } else if (state.selectedUserId !== null && state.userBasicInfo !== null) {
        if (state.userBasicInfo.isApproved && !state.userBasicInfo.isLocked) {
          await lockUser(state.selectedUserId);
        } else {
          await unlockUser(state.selectedUserId);
        }
        rootDispatch.popup.addOperationSuccessfulSnackbar();
        // noinspection ES6MissingAwait
        dispatch.downloadUsers(true);
        await dispatch.downloadUserBasicInfo();
      }
    },
    async downloadUserOfficeRoles(context) {
      const { state, commit } = _moduleActionContext(context);
      if (state.selectedUserId !== null && state.selectedOfficeId !== null) {
        const roles = await getUserOfficeRoles(state.selectedUserId, state.selectedOfficeId);
        commit.setUserOfficeRoles(roles);
      }
    },
    async toggleUserRole(context, roleId: string) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.waitForCompleteAllRequests();
      if (state.selectedUserId !== null && state.selectedOfficeId !== null) {
        const role = state.userOfficeRoles.find((v) => v.id === roleId) as UserRole;
        if (role.userIsInRole) {
          await removeUserRole(state.selectedUserId, state.selectedOfficeId, roleId);
        } else {
          await addUserRole(state.selectedUserId, state.selectedOfficeId, roleId);
        }
        rootDispatch.popup.addOperationSuccessfulSnackbar();
        await dispatch.downloadUserOfficeRoles();
      }
    },
    async downloadAgentCodes(context) {
      const { state, commit } = _moduleActionContext(context);
      if (state.selectedUserId !== null && state.selectedOfficeId !== null) {
        const agentCodes = await getAgentCodes(state.selectedUserId, state.selectedOfficeId);
        commit.setAgentCodes(agentCodes);
      }
    },
    async removeAgentCode(context, agentCodeId: number) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      if (state.selectedUserId !== null && state.selectedOfficeId !== null) {
        await removeAgentCode(agentCodeId, state.selectedUserId, state.selectedOfficeId);
        dispatch.downloadAgentCodes();
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async newAgentCodeDialogOpen(context) {
      const { commit } = _moduleActionContext(context);
      commit.setAgentId("");
      commit.setAgentCodeId(null);
      commit.setAgentSystem("Nie wybrano");
      commit.setIsAgentCodeDialog(true);
    },
    async editAgentCodeDialogOpen(context, agentCode: AgentCode) {
      const { commit } = _moduleActionContext(context);
      commit.setAgentId(agentCode.agentId);
      commit.setAgentCodeId(agentCode.id);
      commit.setAgentSystem(agentCode.system);
      commit.setIsAgentCodeDialog(true);
    },
    async saveAgentCode(context) {
      const { state, dispatch, rootDispatch, commit } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (state.selectedUserId !== null && state.selectedOfficeId !== null) {
        if (state.agentCodeId === null) {
          await addAgentCode(state.selectedUserId, state.selectedOfficeId, state.agentId, state.agentSystem);
        } else {
          await editAgentCode(
            state.agentCodeId,
            state.selectedUserId,
            state.selectedOfficeId,
            state.agentId,
            state.agentSystem
          );
        }
        dispatch.downloadAgentCodes();
        commit.setIsAgentCodeDialog(false);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async downloadCashboxes(context) {
      const { state, commit } = _moduleActionContext(context);
      if (state.selectedUserId !== null && state.selectedOfficeId !== null) {
        const cashboxes = await getUserCashboxes(state.selectedUserId, state.selectedOfficeId);
        commit.setCashboxes(cashboxes);
      }
    },
    async toggleCashboxAssignment(context, cashbox: UserCashbox) {
      const { rootDispatch, state, dispatch } = _moduleActionContext(context);
      if (state.selectedUserId !== null && state.selectedOfficeId !== null) {
        rootDispatch.failIfRequestsInProgress();
        if (cashbox.isAssign) {
          await removeUserProfileCashbox(state.selectedUserId, state.selectedOfficeId, cashbox.id);
        } else {
          await addUserProfileCashbox(state.selectedUserId, state.selectedOfficeId, cashbox.id);
        }
        rootDispatch.popup.addOperationSuccessfulSnackbar();
        dispatch.downloadCashboxes();
      }
    },
    async setCashboxAsDefault(context, cashboxId: number) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      if (state.selectedUserId !== null && state.selectedOfficeId !== null) {
        rootDispatch.failIfRequestsInProgress();
        await addUserProfileDefaultCashbox(state.selectedUserId, state.selectedOfficeId, cashboxId);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
        dispatch.downloadCashboxes();
      }
    },
    async downloadUserTemporaryRoles(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedUserId && state.selectedOfficeId) {
        const response = await getUserTemporaryRoles(state.selectedOfficeId, state.selectedUserId);
        commit.setTemporaryRoles(response);
      }
    },
    async downloadRoles(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedOfficeId) {
        const response = await getRoles(state.selectedOfficeId);
        commit.setRoles(response);
      }
    },
    async addTemporaryRole(context) {
      const { state, rootDispatch, dispatch } = _moduleActionContext(context);
      if (state.selectedUserId && state.editTemporaryRole) {
        await addTemporaryRole(state.selectedUserId, state.editTemporaryRole);
        await rootDispatch.popup.addSavedOkSnackbar();
        await dispatch.downloadUserTemporaryRoles();
      }
    },
    async editTemporaryRole(context) {
      const { state, rootDispatch, dispatch } = _moduleActionContext(context);
      if (state.editTemporaryRole) {
        await editTemporaryRole(state.editTemporaryRole);
        await rootDispatch.popup.addSavedOkSnackbar();
        await dispatch.downloadUserTemporaryRoles();
      }
    },
    async toggleTemporaryRole(context, param: number) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      await toggleTemporaryRole(param);
      await rootDispatch.popup.addSavedOkSnackbar();
      await dispatch.downloadUserTemporaryRoles();
    },
    async openEditRoleDialog(context, model: EditTemporaryRoleModel) {
      const { commit } = _moduleActionContext(context);
      commit.setEditTemporaryRole(model);
    },
  },
});

export default AdministrationUsersModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, AdministrationUsersModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationUsersModule);
